import React, { useState } from 'react';
import { PaymentDetailDrawer } from './PaymentDetailDrawer';
import { RefundPaymentDialog } from './RefundPaymentDialog';
import { useVoidPayment } from '../hooks/useVoidPayment';
import { buildVoidRequest } from '../utils/paymentUtils';
import type { Payment } from '../types/payment.types';

/**
 * A payment's detail drawer with its Void and Refund actions wired, for any list of payments (the Payments page and
 * the Transactions of a business). It adds no UI of its own: PaymentDetailDrawer, RefundPaymentDialog and
 * useVoidPayment as the Payments page has always used them. Void and the refund run against the PAYMENT's own
 * business (the tenant-wide listing carries it); `selectedBusinessId` is only the fallback for a listing that omits it.
 */
export const PaymentActionsHost: React.FC<{
  payment: Payment | null;
  onClose: () => void;
  selectedBusinessId?: string | null;
}> = ({ payment, onClose, selectedBusinessId }) => {
  const voidMutation = useVoidPayment();
  // SAM-466: the payment being refunded (the drawer closes first; see RefundPaymentDialog).
  const [refundPayment, setRefundPayment] = useState<Payment | null>(null);

  const handleVoid = () => {
    if (!payment) return;
    const request = buildVoidRequest(payment, selectedBusinessId);
    if (!request) return;
    voidMutation.mutate(request, { onSuccess: onClose });
  };

  return (
    <>
      <PaymentDetailDrawer
        isOpen={!!payment}
        onClose={onClose}
        payment={payment}
        onConfirmVoid={handleVoid}
        onRefund={(p) => {
          onClose();
          setRefundPayment(p);
        }}
        isLoading={voidMutation.isPending}
      />
      <RefundPaymentDialog
        isOpen={!!refundPayment}
        payment={refundPayment}
        businessId={refundPayment?.business_id || selectedBusinessId || null}
        onClose={() => setRefundPayment(null)}
      />
    </>
  );
};
